import { Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { SearchTermOrigin } from "@/types/search-term";
import { HoverableKeyword } from "./HoverableKeyword";

interface SearchTermEntryProps {
  idx: number;
  term: string;
  limit: number;
  isSingle: boolean;
  isEditing: boolean;
  readOnly: boolean;
  originMap: Map<string, SearchTermOrigin>;
  // FR-7: this slot's index is in the set's `regeneration_slots` claim. Its words are
  // replaced by the "Regenerating search terms…" placeholder, regardless of `isEditing`
  // (Edit is hidden for the whole run at the card level, and the card now withdraws
  // selection/the bulk bar for the whole edit too, so a run cannot be entered from inside an
  // edit either -- the two never overlap in practice).
  isRegenerating?: boolean;
  // FR-2/FR-10: whether this slot offers a selection checkbox at all. The card decides this
  // (selection prop, status, in-flight/stale) — this component only renders what it is told.
  showCheckbox?: boolean;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  onTermChange: (idx: number, value: string) => void;
  onCopy: (text: string, label: string) => void;
  onAddToProhibited?: (word: string) => void;
}

export function SearchTermEntry({
  idx,
  term,
  limit,
  isSingle,
  isEditing,
  readOnly,
  originMap,
  isRegenerating = false,
  showCheckbox = false,
  checked = false,
  onCheckedChange,
  onTermChange,
  onCopy,
  onAddToProhibited,
}: SearchTermEntryProps) {
  const charCount = term.length;
  const isOverLimit = charCount > limit;
  const words = term.split(/\s+/).filter(Boolean);

  return (
    <div className="space-y-1">
      {!isSingle && (
        <div className={`text-xs font-medium flex items-center gap-1.5 ${isOverLimit ? "text-destructive" : "text-muted-foreground"}`}>
          {showCheckbox && (
            <Checkbox
              aria-label={`Select set ${idx + 1}`}
              checked={checked}
              onCheckedChange={(value) => onCheckedChange?.(value === true)}
            />
          )}
          <span>Set {idx + 1} — {charCount} of {limit}</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 ml-1"
            onClick={() => onCopy(term, `Set ${idx + 1}`)}
          >
            <Copy className="h-3 w-3" />
          </Button>
        </div>
      )}
      {isSingle && showCheckbox && (
        <div className="flex items-center gap-1.5">
          <Checkbox
            aria-label="Select search term set"
            checked={checked}
            onCheckedChange={(value) => onCheckedChange?.(value === true)}
          />
        </div>
      )}
      <div className="relative group">
        {isRegenerating ? (
          <div className="select-text rounded-md border border-border bg-card px-3 py-2 min-h-[60px] flex items-start content-start">
            <span className="text-sm text-muted-foreground animate-pulse">
              Regenerating search terms…
            </span>
          </div>
        ) : isEditing && !readOnly ? (
          <Textarea
            value={term}
            onChange={(e) => onTermChange(idx, e.target.value)}
            className={`text-sm bg-muted/50 resize-none min-h-[120px] pr-10 ${
              isOverLimit ? "border-destructive focus-visible:ring-destructive" : ""
            }`}
            rows={isSingle ? 4 : 6}
          />
        ) : (
          <div
            className={`select-text rounded-md border border-border bg-card px-3 py-2 min-h-[60px] flex flex-wrap gap-x-1.5 gap-y-1 items-start content-start ${
              isOverLimit ? "border-destructive" : ""
            }`}
          >
            {words.map((word, wIdx) => (
              <HoverableKeyword
                key={`${wIdx}-${word}`}
                word={word}
                origin={originMap.get(word.toLowerCase())}
                onAddToProhibited={onAddToProhibited}
              />
            ))}
          </div>
        )}
      </div>
      {isSingle && (
        <div className={`text-xs flex items-center gap-1.5 ${isOverLimit ? "text-destructive" : "text-muted-foreground"}`}>
          <span>{charCount} / {limit} characters</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 ml-1"
            onClick={() => onCopy(term, "Search terms")}
          >
            <Copy className="h-3 w-3" />
          </Button>
        </div>
      )}
    </div>
  );
}
